<template>
  <VaDropdown :offset="[13, 0]" class="notification-dropdown" stick-to-edges :close-on-content-click="false">
    <template #anchor>
      <button type="button" class="notification-dropdown__trigger" aria-label="Notifications">
        <VaBadge overlap>
          <!-- <template #text> 2+</template> -->
          <VaIconNotification class="notification-dropdown__icon" />
        </VaBadge>
      </button>
    </template>
    <VaDropdownContent class="h-full sm:max-w-[420px] sm:h-auto">
      <section class="sm:max-h-[320px] p-4 overflow-auto">
        <VaList class="space-y-1 mb-2">
          <template v-for="(item, index) in notificationsWithRelativeTime" :key="item.id">
            <VaListItem class="text-base">
              <VaListItemSection icon class="mx-0 p-0">
                <VaIcon :name="item.icon" color="secondary" />
              </VaListItemSection>
              <VaListItemSection>
                {{ item.message }}
              </VaListItemSection>
              <VaListItemSection icon class="mx-1">
                {{ item.updateTimestamp }}
              </VaListItemSection>
            </VaListItem>
            <VaListSeparator v-if="item.separator && index !== notificationsWithRelativeTime.length - 1" class="mx-3" />
          </template>
        </VaList>

        <VaButton preset="primary" class="w-full" @click="displayAllNotifications = !displayAllNotifications"
          >{{ displayAllNotifications ? t('notifications.less') : t('notifications.all') }}
        </VaButton>
      </section>
    </VaDropdownContent>
  </VaDropdown>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import VaIconNotification from '../../../icons/VaIconNotification.vue'

const { t, locale } = useI18n()

const baseNumberOfVisibleNotifications = 4
const rtf = new Intl.RelativeTimeFormat(locale.value, { style: 'short' })
const displayAllNotifications = ref(false)

interface INotification {
  message: string
  icon: string
  id: number
  separator?: boolean
  updateTimestamp: Date
}

const notifications: INotification[] = [
  // {
  //   message: '4 pending requests',
  //   icon: 'favorite_outline',
  //   id: 1,
  //   separator: true,
  //   updateTimestamp: makeDateFromNow(-3 * 60 * 1000),
  // },
  // {
  //   message: '3 new reports',
  //   icon: 'calendar_today',
  //   id: 2,
  //   separator: true,
  //   updateTimestamp: makeDateFromNow(-12 * 60 * 60 * 1000),
  // },
  // {
  //   message: 'Whoops! Your trial period has expired.',
  //   icon: 'error_outline',
  //   id: 3,
  //   separator: true,
  //   updateTimestamp: makeDateFromNow(-2 * 24 * 60 * 60 * 1000),
  // },
  // {
  //   message: 'It looks like your timezone is set incorrectly, please change it to avoid issues with Memory.',
  //   icon: 'schedule',
  //   id: 4,
  //   updateTimestamp: makeDateFromNow(-2 * 7 * 24 * 60 * 60 * 1000),
  // },
  // {
  //   message: '2 new team members added',
  //   icon: 'group_add',
  //   id: 5,
  //   separator: false,
  //   updateTimestamp: makeDateFromNow(-3 * 60 * 1000),
  // },
  // {
  //   message: 'Monthly budget exceeded by 10%',
  //   icon: 'trending_up',
  //   id: 6,
  //   separator: true,
  //   updateTimestamp: makeDateFromNow(-3 * 24 * 60 * 60 * 1000),
  // },
  // {
  //   message: '7 tasks are approaching their deadlines',
  //   icon: 'alarm',
  //   id: 7,
  //   separator: false,
  //   updateTimestamp: makeDateFromNow(-5 * 60 * 60 * 1000),
  // },
  // {
  //   message: 'New software update available',
  //   icon: 'system_update',
  //   id: 8,
  //   separator: true,
  //   updateTimestamp: makeDateFromNow(-1 * 24 * 60 * 60 * 1000),
  // },
].sort((a: INotification, b: INotification) => {
  if (!a.updateTimestamp || !b.updateTimestamp) return 0
  return new Date(b.updateTimestamp).getTime() - new Date(a.updateTimestamp).getTime()
})

const TIME_NAMES = {
  second: 1000,
  minute: 1000 * 60,
  hour: 1000 * 60 * 60,
  day: 1000 * 60 * 60 * 24,
  week: 1000 * 60 * 60 * 24 * 7,
  month: 1000 * 60 * 60 * 24 * 30,
  year: 1000 * 60 * 60 * 24 * 365,
}

const getTimeName = (differenceTime: number) => {
  return Object.keys(TIME_NAMES).reduce(
    (acc, key) => (TIME_NAMES[key as keyof typeof TIME_NAMES] < differenceTime ? key : acc),
    'month',
  ) as keyof typeof TIME_NAMES
}

const notificationsWithRelativeTime = computed(() => {
  const list = displayAllNotifications.value ? notifications : notifications.slice(0, baseNumberOfVisibleNotifications)

  return list.map((item, index) => {
    const timeDifference = Math.round(new Date().getTime() - new Date(item.updateTimestamp).getTime())
    const timeName = getTimeName(timeDifference)

    let separator = false

    const nextItem = list[index + 1]
    if (nextItem) {
      const nextItemDifference = Math.round(new Date().getTime() - new Date(nextItem.updateTimestamp).getTime())
      const nextItemTimeName = getTimeName(nextItemDifference)

      if (timeName !== nextItemTimeName) {
        separator = true
      }
    }

    return {
      ...item,
      updateTimestamp: rtf.format(-1 * Math.round(timeDifference / TIME_NAMES[timeName]), timeName),
      separator,
    }
  })
})
</script>

<style lang="scss" scoped>
.notification-dropdown {
  cursor: pointer;

  &__trigger {
    width: 36px;
    height: 36px;

    display: inline-flex;
    align-items: center;
    justify-content: center;

    border: 0;
    border-radius: 50%;

    background: #ffffff;
    color: #475569;

    cursor: pointer;

    transition:
      background-color 0.2s ease,
      color 0.2s ease,
      transform 0.2s ease;

    &:hover {
      background: rgba(var(--va-primary-rgb, 15, 34, 204), 0.1);
      color: var(--va-primary);
      transform: translateY(-1px);
    }

    &:active {
      transform: scale(0.96);
    }
  }

  :deep(.va-dropdown__content) {
    background: var(--va-background-secondary);
    border-radius: 0.75rem;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
    border: 1px solid var(--va-background-primary);
    animation: dropdown-enter 0.2s ease;

    @keyframes dropdown-enter {
      from {
        opacity: 0;
        transform: translateY(-8px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    section {
      padding: 1rem;
    }

    .va-list-item {
      transition: all 0.2s ease;
      border-radius: 0.375rem;
      padding: 0.5rem 0.75rem;

      &:hover {
        background: linear-gradient(135deg, rgba(15, 34, 204, 0.1) 0%, rgba(79, 70, 229, 0.05) 100%);
        transform: translateX(4px);
      }

      :deep(.va-icon) {
        transition: all 0.2s ease;
        color: var(--va-primary) !important;
      }
    }

    .va-list-separator {
      margin: 0.5rem 0;
      opacity: 0.2;
    }

    :deep(.va-button) {
      transition: all 0.3s ease;

      &:hover {
        background: linear-gradient(135deg, rgba(15, 34, 204, 0.9) 0%, rgba(79, 70, 229, 0.9) 100%);
        transform: translateY(-2px);
      }
    }
  }

  .notification-dropdown__icon {
    position: relative;
    display: flex;
    align-items: center;
    transition: all 0.3s ease;
  }

  .va-dropdown__anchor {
    display: inline-block;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
    }
  }
}
</style>
